import { useEffect, useRef } from 'preact/hooks';
import { useWidgetAnalytics } from '../../analytics/useWidgetAnalytics';
import type { WidgetTabId } from '../../widgetConfig';
import type { JourneyWidgetPage, JourneyWidgetPageState } from './pageState';

interface UseJourneyWidgetTrackingOptions {
  pageState: JourneyWidgetPageState;
  activePage: JourneyWidgetPage;
  initialTab: WidgetTabId;
  initialOrigin: string;
  initialDestination: string;
  initialDepartureStopId: string;
}

/**
 * Tracks widget-level lifecycle analytics: the initial load and activity-tab
 * switches. Kept separate from JourneyWidgetTabs so that component can stay
 * focused on composing navigation and rendering tabs.
 */
export function useJourneyWidgetTracking({
  pageState,
  activePage,
  initialTab,
  initialOrigin,
  initialDestination,
  initialDepartureStopId,
}: UseJourneyWidgetTrackingOptions): void {
  const { track } = useWidgetAnalytics();
  const hasTrackedLoadedRef = useRef(false);
  const previousActivityRef = useRef(pageState.activeActivity);

  useEffect(() => {
    if (hasTrackedLoadedRef.current) {
      return;
    }

    hasTrackedLoadedRef.current = true;

    track({
      name: 'widget_loaded',
      activity: pageState.activeActivity,
      page: activePage.kind,
      payload: {
        initialTab,
        hasInitialOrigin: Boolean(initialOrigin),
        hasInitialDestination: Boolean(initialDestination),
        hasDepartureStop: Boolean(initialDepartureStopId),
      },
    });
  }, [
    activePage.kind,
    initialDepartureStopId,
    initialDestination,
    initialOrigin,
    initialTab,
    pageState.activeActivity,
    track,
  ]);

  useEffect(() => {
    if (previousActivityRef.current === pageState.activeActivity) {
      return;
    }

    previousActivityRef.current = pageState.activeActivity;

    track({
      name: 'activity_changed',
      activity: pageState.activeActivity,
      page: activePage.kind,
      payload: {
        activity: pageState.activeActivity,
      },
    });
  }, [activePage.kind, pageState.activeActivity, track]);
}
